import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, todayStr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, MapPin, Loader2, CheckCircle2, X, FileText } from "lucide-react";
import { toast } from "sonner";

const PAY = ["Cash", "Company Card", "Bank Transfer", "Personal (Reimburse)"];

export default function AddExpense() {
  const { settings, user, isManager } = useAuth();
  const nav = useNavigate();
  const cur = settings.currency || "£";
  const fileRef = useRef();
  const [sites, setSites] = useState([]);
  const [cats, setCats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [f, setF] = useState({
    site_id: "", purchase_date: todayStr(), purchase_time: new Date().toTimeString().slice(0, 5),
    amount: "", merchant: "", category: "", description: "", payment_method: "Cash",
    notes: "", location_name: "", location_address: "", location_lat: null, location_lng: null,
    staff_id: "",
  });

  useEffect(() => {
    api.get("/sites").then((r) => { setSites(r.data); if (r.data.length === 1) upd("site_id", r.data[0].id); });
    api.get("/categories?active_only=true").then((r) => setCats(r.data));
    if (isManager) api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role === "staff")));
  }, []);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const pickReceipt = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReceiptFile(file);
    if (file.type.startsWith("image/")) setReceiptPreview(URL.createObjectURL(file));
    else setReceiptPreview(null);
  };

  const captureGPS = () => {
    if (!navigator.geolocation) { toast.error("GPS not available on this device"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        upd("location_lat", pos.coords.latitude);
        upd("location_lng", pos.coords.longitude);
        if (!f.location_name) upd("location_name", f.merchant || "Current location");
        toast.success("Location captured");
        setGpsBusy(false);
      },
      () => { toast.error("Could not get location. Enter it manually."); setGpsBusy(false); },
      { timeout: 8000 }
    );
  };

  const save = async (asDraft) => {
    if (!f.site_id || !f.amount || !f.merchant || !f.category) {
      toast.error("Please fill in home, amount, merchant and category");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, amount: parseFloat(f.amount), status: asDraft ? "draft" : "submitted",
        staff_id: f.staff_id || undefined };
      const { data } = await api.post("/expenses", payload);
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        await api.post(`/expenses/${data.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(asDraft ? "Saved as draft" : "Expense submitted for approval");
      nav("/expenses");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Add Expense</h1>
        <p className="text-slate-500 text-sm mt-1">Record a purchase in under a minute.</p>
      </div>

      <Card className="border-border p-5 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Home</Label>
            <Select value={f.site_id} onValueChange={(v) => upd("site_id", v)}>
              <SelectTrigger data-testid="ae-site" className="mt-1.5 h-12"><SelectValue placeholder="Select home" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {money(s.remaining, cur)} left</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Amount ({cur})</Label>
            <Input data-testid="ae-amount" type="number" step="0.01" inputMode="decimal" value={f.amount} onChange={(e) => upd("amount", e.target.value)} placeholder="0.00" className="mt-1.5 h-12 text-lg font-mono" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger data-testid="ae-category" className="mt-1.5 h-12"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Merchant / Shop</Label>
            <Input data-testid="ae-merchant" value={f.merchant} onChange={(e) => upd("merchant", e.target.value)} placeholder="e.g. Tesco" className="mt-1.5 h-12" />
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={f.payment_method} onValueChange={(v) => upd("payment_method", v)}>
              <SelectTrigger data-testid="ae-payment" className="mt-1.5 h-12"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Purchase Date</Label>
            <Input data-testid="ae-date" type="date" value={f.purchase_date} onChange={(e) => upd("purchase_date", e.target.value)} className="mt-1.5 h-12" />
          </div>
          <div>
            <Label>Purchase Time</Label>
            <Input data-testid="ae-time" type="time" value={f.purchase_time} onChange={(e) => upd("purchase_time", e.target.value)} className="mt-1.5 h-12" />
          </div>

          <div className="sm:col-span-2">
            <Label>Description / Items Purchased</Label>
            <Textarea data-testid="ae-description" value={f.description} onChange={(e) => upd("description", e.target.value)} placeholder="Milk, bread, fruit, chicken, cereal…" className="mt-1.5" rows={2} />
          </div>

          {isManager && (
            <div className="sm:col-span-2">
              <Label>Staff Member (recording on behalf of)</Label>
              <Select value={f.staff_id} onValueChange={(v) => upd("staff_id", v)}>
                <SelectTrigger data-testid="ae-staff" className="mt-1.5 h-12"><SelectValue placeholder={user.name + " (me)"} /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Receipt */}
        <div>
          <Label>Receipt</Label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={pickReceipt} data-testid="ae-receipt-input" />
          {!receiptFile ? (
            <button type="button" data-testid="ae-receipt-btn" onClick={() => fileRef.current.click()}
              className="mt-1.5 w-full border-2 border-dashed border-slate-300 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-500 hover:border-slate-400 hover:bg-slate-50 transition-colors duration-200">
              <Camera className="h-7 w-7" />
              <span className="text-sm font-medium">Take photo or upload receipt</span>
              <span className="text-xs text-slate-400">PNG, JPEG or PDF</span>
            </button>
          ) : (
            <div className="mt-1.5 relative border border-border rounded-xl p-3 flex items-center gap-3">
              {receiptPreview ? <img src={receiptPreview} alt="receipt" className="h-16 w-16 object-cover rounded-lg" /> : <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center"><FileText className="h-6 w-6 text-slate-500" /></div>}
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{receiptFile.name}</div><div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Attached</div></div>
              <button type="button" onClick={() => { setReceiptFile(null); setReceiptPreview(null); }} className="text-slate-400 hover:text-red-500"><X className="h-5 w-5" /></button>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="space-y-3 rounded-xl bg-slate-50 border border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><MapPin className="h-4 w-4" /> Location</div>
            <Button type="button" variant="outline" size="sm" data-testid="ae-gps" onClick={captureGPS} disabled={gpsBusy}>
              {gpsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Use my location"}
            </Button>
          </div>
          <Input data-testid="ae-location-name" value={f.location_name} onChange={(e) => upd("location_name", e.target.value)} placeholder="Location name (e.g. Tesco Extra, Sittingbourne)" className="bg-white" />
          <Input data-testid="ae-location-address" value={f.location_address} onChange={(e) => upd("location_address", e.target.value)} placeholder="Address (optional)" className="bg-white" />
          {f.location_lat && <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> GPS: {f.location_lat.toFixed(4)}, {f.location_lng.toFixed(4)}</div>}
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea data-testid="ae-notes" value={f.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Anything the manager should know…" className="mt-1.5" rows={2} />
        </div>
      </Card>

      <div className="flex gap-3 sticky bottom-20 lg:bottom-0">
        <Button variant="outline" className="flex-1 h-12" data-testid="ae-draft" disabled={saving} onClick={() => save(true)}>Save Draft</Button>
        <Button className="flex-1 h-12 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="ae-submit" disabled={saving} onClick={() => save(false)}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Expense"}
        </Button>
      </div>
    </div>
  );
}
